import moment from "moment";

export default class Calendar {

    /**
     * Moves fixed-date holidays that land on weekends to their observed weekday.
     * Saturday holidays are observed on Friday, Sunday holidays on Monday.
     * @param {moment} holiday
     * @returns {moment}
     */
    static getObservedHoliday(holiday) {
        const observedHoliday = holiday.clone();

        if (observedHoliday.isoWeekday() === 6) {
            return observedHoliday.subtract(1, "day");
        }

        if (observedHoliday.isoWeekday() === 7) {
            return observedHoliday.add(1, "day");
        }

        return observedHoliday;
    }

    /**
     * Adds days to the current local date.
     * @param {Object} options - {number daysToAdd, boolean useBusinessDays}
     * @returns {string} ISO 8601 string in local time.
     */
    static addDays(options) {
        let date = moment();
        if (!options.useBusinessDays) {
            date = date.add(options.daysToAdd, "days");
            return date.toISOString();
        }

        // Business days: skip weekends and holidays Adding a few days to
        // holidayEndDate to make sure we capture all possible holidays that
        // might occur. Should be suitable for moderate daysToAdd values.
        let daysAdded = 0;
        const holidayBeginDate = options.daysToAdd >= 0
            ? date.clone()
            : date.clone().add(options.daysToAdd - 90, "days");
        const holidayEndDate = options.daysToAdd >= 0
            ? date.clone().add(options.daysToAdd + 90, "days")
            : date.clone();
        const holidays = Calendar.getHolidaysInRange(holidayBeginDate, holidayEndDate);

        while (daysAdded < Math.abs(options.daysToAdd)) {
            date = date.add(options.daysToAdd >= 0 ? 1 : -1, "days");
            const isWeekend = date.isoWeekday() > 5;
            const isHoliday = holidays.includes(date.format("YYYY-MM-DD"));
            if (!isWeekend && !isHoliday) {
                daysAdded++;
            }
        }
        return date.toISOString();
    }

    /**
     * Returns an array of holiday dates between begin and end date (inclusive).
     * @param {moment} beginDate
     * @param {moment} endDate
     * @returns {string[]} Array of "YYYY-MM-DD" dates
     */
    static getHolidaysInRange(beginDate, endDate) {
        const begin = moment(beginDate);
        const end = moment(endDate);
        const holidays = new Set();

        for (let year = begin.year() - 1; year <= end.year() + 1; year++) {
            const fixedHolidays = [
                Calendar.getObservedHoliday(moment(`January 1, ${year}`, "MMMM D, YYYY")), // New Year's Day
                Calendar.getObservedHoliday(moment(`July 4, ${year}`, "MMMM D, YYYY")), // Independence Day
                Calendar.getObservedHoliday(moment(`December 25, ${year}`, "MMMM D, YYYY")), // Christmas Day
                moment(`January 8, 2026`, "MMMM D, YYYY"), // Annual Conference Thursday
                moment(`January 9, 2026`, "MMMM D, YYYY"), // Annual Conference Friday
            ];

            const memorialDay = moment({ year, month: 4 }).endOf('month').isoWeekday(1); // Last Monday in May
            const laborDay = moment({ year, month: 8, day: 1 }).day(1); // First Monday in September
            const thanksgiving = moment({ year }) // Fourth Thursday in November
                .month(10)
                .startOf('month')
                .add((11 - moment({ year }).month(10).startOf('month').isoWeekday()) % 7 + 21, 'days');
            const dayAfterThanksgiving = thanksgiving.clone().add(1, 'day');

            const allHolidays = [...fixedHolidays, memorialDay, laborDay, thanksgiving, dayAfterThanksgiving];

            allHolidays.forEach(holiday => {
                if (holiday.isBetween(begin, end, null, "[]")) {
                    holidays.add(holiday.format("YYYY-MM-DD"));
                }
            });
        }

        return [...holidays];
    }
}
